import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n";

// Illustrated map hotspots — percentages over campania-illustrata.jpg (1268x1241)
// Each point links to the matching experience or destination package
const HOTSPOTS = [
  { id: "caserta", x: 21.0, y: 16.5, to: "/experiences/caserta-royal-palace", label: { it: "Caserta · Reggia", en: "Caserta · Royal Palace" } },
  { id: "benevento", x: 35.0, y: 20.5, to: "/experiences?destination=apennines", label: { it: "Benevento · Arco di Traiano", en: "Benevento · Trajan's Arch" } },
  { id: "avellino", x: 47.5, y: 20.5, to: "/experiences?destination=apennines", label: { it: "Avellino · Irpinia", en: "Avellino · Irpinia" } },
  { id: "santagata", x: 54.0, y: 28.5, to: "/experiences?destination=apennines", label: { it: "Sant'Agata de' Goti", en: "Sant'Agata de' Goti" } },
  { id: "pozzuoli", x: 18.0, y: 27.0, to: "/experiences/pozzuoli-ancient-rome", label: { it: "Pozzuoli · Anfiteatro Flavio", en: "Pozzuoli · Flavian Amphitheatre" } },
  { id: "lago-averno", x: 13.5, y: 29.0, to: "/experiences/lago-averno-monte-nuovo", label: { it: "Lago d'Averno · Monte Nuovo", en: "Lake Avernus · Monte Nuovo" } },
  { id: "solfatara", x: 16.0, y: 24.0, to: "/experiences/vulcani-roma-aperitivo", label: { it: "Solfatara · Vulcani & Roma", en: "Solfatara · Volcanoes & Rome" } },
  { id: "bacoli", x: 9.5, y: 32.5, to: "/experiences/piscina-mirabilis-experience", label: { it: "Bacoli · Piscina Mirabilis", en: "Bacoli · Piscina Mirabilis" } },
  { id: "napoli", x: 29.0, y: 32.0, to: "/experiences/historic-naples", label: { it: "Napoli · Centro Storico", en: "Naples · Historic Centre" } },
  { id: "vesuvio", x: 42.5, y: 33.5, to: "/experiences/vesuvius-crater-hike", label: { it: "Vesuvio · Parco Nazionale", en: "Vesuvius · National Park" } },
  { id: "ischia", x: 7.5, y: 41.5, to: "/experiences/ischia-by-boat", label: { it: "Ischia · Castello Aragonese", en: "Ischia · Aragonese Castle" } },
  { id: "pompei", x: 35.5, y: 41.5, to: "/experiences/pompeii-private-tour", label: { it: "Pompei · Scavi", en: "Pompeii · Ruins" } },
  { id: "ercolano", x: 48.0, y: 45.5, to: "/experiences?destination=pompeii", label: { it: "Ercolano · Scavi", en: "Herculaneum · Ruins" } },
  { id: "sorrento", x: 21.0, y: 49.5, to: "/experiences/sorrento-walking-tour", label: { it: "Sorrento · Centro & Limoncello", en: "Sorrento · Old Town & Limoncello" } },
  { id: "amalfi", x: 36.5, y: 52.5, to: "/experiences?destination=amalfi-coast", label: { it: "Amalfi · Duomo", en: "Amalfi · Cathedral" } },
  { id: "ravello", x: 45.0, y: 52.5, to: "/experiences?destination=amalfi-coast", label: { it: "Ravello · Villa Rufolo", en: "Ravello · Villa Rufolo" } },
  { id: "salerno", x: 58.0, y: 53.0, to: "/experiences?destination=amalfi-coast", label: { it: "Salerno · Cattedrale", en: "Salerno · Cathedral" } },
  { id: "procida", x: 11.5, y: 54.5, to: "/experiences/procida-by-boat", label: { it: "Procida · Corricella", en: "Procida · Corricella" } },
  { id: "paestum", x: 66.0, y: 59.0, to: "/create", label: { it: "Paestum · Templi", en: "Paestum · Temples" } },
  { id: "positano", x: 28.0, y: 59.5, to: "/experiences?destination=amalfi-coast", label: { it: "Positano · Centro Storico", en: "Positano · Old Town" } },
  { id: "capaccio", x: 63.0, y: 68.0, to: "/create", label: { it: "Capaccio · Scavi di Paestum", en: "Capaccio · Paestum Ruins" } },
  { id: "ligalli", x: 32.5, y: 73.5, to: "/experiences/capri-private-boat", label: { it: "Li Galli · Isole", en: "Li Galli · Islands" } },
  { id: "cilento", x: 66.5, y: 75.5, to: "/create", label: { it: "Cilento · Parco Nazionale", en: "Cilento · National Park" } },
  { id: "capri", x: 22.5, y: 77.5, to: "/experiences/capri-private-boat", label: { it: "Capri · Villa Jovis", en: "Capri · Villa Jovis" } },
  { id: "velia", x: 63.5, y: 83.5, to: "/create", label: { it: "Velia · Scavi", en: "Velia · Ruins" } },
  { id: "palinuro", x: 47.0, y: 87.5, to: "/create", label: { it: "Palinuro · Grotta Azzurra", en: "Palinuro · Blue Grotto" } },
  { id: "castellabate", x: 74.0, y: 89.0, to: "/create", label: { it: "Castellabate · Centro Storico", en: "Castellabate · Old Town" } },
];

export default function CampaniaMap() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);

  return (
    <div className="relative border border-stone2/60 overflow-hidden select-none" data-testid="campania-map">
      <img
        src="/images/campania-illustrata.jpg"
        alt={lang === "it" ? "Mappa illustrata della Campania" : "Illustrated map of Campania"}
        loading="lazy"
        className="w-full h-auto block"
      />

      {HOTSPOTS.map((h) => (
        <button
          key={h.id}
          data-testid={`map-pin-${h.id}`}
          onMouseEnter={() => setActive(h.id)}
          onMouseLeave={() => setActive(null)}
          onClick={() => navigate(h.to)}
          className="absolute group z-10"
          style={{ left: `${h.x}%`, top: `${h.y}%`, transform: "translate(-50%, -50%)" }}
          aria-label={h.label[lang] || h.label.en}
        >
          <span className={`block w-4 h-4 md:w-5 md:h-5 rounded-full border-2 border-ivory shadow-md transition-all duration-300 ${active === h.id ? "bg-terracotta scale-125" : "bg-medblue/90 group-hover:scale-110"}`}>
            <span className="absolute inset-0 -m-1.5 rounded-full border border-medblue/40 opacity-0 group-hover:opacity-100 group-hover:animate-ping pointer-events-none" />
          </span>
          <span
            className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 whitespace-nowrap bg-ink/90 text-ivory text-[9px] md:text-[10px] font-medium uppercase tracking-[0.12em] px-2.5 py-1.5 pointer-events-none transition-all duration-300 flex items-center gap-1.5 ${
              active === h.id ? "opacity-100 -translate-y-0.5" : "opacity-0"
            }`}
          >
            {h.label[lang] || h.label.en} <ArrowRight size={10} strokeWidth={2} />
          </span>
        </button>
      ))}

      <p className="absolute top-3 left-3 text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-ink/60 bg-ivory/80 px-2 py-1" data-testid="map-hint">
        {t("map_hint")}
      </p>
    </div>
  );
}
